/**
 * Allure WebAuthn / Passkey utilities.
 *
 * Wraps `@simplewebauthn/browser` with a small allure-aware API:
 *  - `isPasskeySupportedOnThisDevice()` — feature + platform-authenticator detection.
 *  - `hasLocalPasskeyFlag()` / `setLocalPasskeyFlag(true|false)` — UI hint persisted in
 *    localStorage so we can short-circuit to the unlock screen on cold launch
 *    without an API round-trip. The server is still the source of truth.
 *  - `enrollPasskey({ token, deviceLabel })` — full register-begin → register-finish
 *    handshake. Returns the new credential record.
 *  - `unlockWithPasskey()` — full authenticate-begin → authenticate-finish
 *    handshake. Returns `{ access_token, user }` so the caller can call
 *    `completeLogin()` exactly the way password / Google sign-in does.
 *  - `listPasskeys(token)` / `revokePasskey(token, id)` — manage devices.
 */
import axios from 'axios';
import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import { mirrorLocalKey } from './authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCAL_FLAG_KEY = 'allure_has_passkey';

export const hasLocalPasskeyFlag = () => {
  try {
    return window.localStorage.getItem(LOCAL_FLAG_KEY) === 'true';
  } catch {
    return false;
  }
};

export const setLocalPasskeyFlag = (value) => {
  try {
    if (value) {
      window.localStorage.setItem(LOCAL_FLAG_KEY, 'true');
    } else {
      window.localStorage.removeItem(LOCAL_FLAG_KEY);
    }
  } catch {
    /* noop */
  }
  // Mirror to Capacitor Preferences so the enrolment flag survives an
  // Android APK update — otherwise the UnlockPage boot short-circuit
  // won't fire after an update.
  mirrorLocalKey(LOCAL_FLAG_KEY, value ? 'true' : null);
};

/** Resolves to true only when WebAuthn AND a platform authenticator are
 *  available — i.e. the device can actually do fingerprint / Face ID /
 *  device-PIN unlock. */
export const isPasskeySupportedOnThisDevice = async () => {
  try {
    if (!browserSupportsWebAuthn()) return false;
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};

/** Quick boolean — does the browser support WebAuthn at all (regardless of
 *  whether the device has a platform authenticator)? Used to gate the UI
 *  for showing the enrollment card so we don't flash it on unsupported
 *  browsers. */
export const browserSupportsPasskeys = () => {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

const _authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

export const enrollPasskey = async ({ token, deviceLabel = '' }) => {
  const beginResp = await axios.post(
    `${API}/webauthn/register/begin`,
    {},
    { headers: _authHeaders(token) },
  );
  // The backend returns options exactly in the shape `startRegistration`
  // wants; no further unwrapping needed.
  const attestation = await startRegistration({ optionsJSON: beginResp.data });
  const finishResp = await axios.post(
    `${API}/webauthn/register/finish`,
    { credential: attestation, device_label: deviceLabel },
    { headers: _authHeaders(token) },
  );
  setLocalPasskeyFlag(true);
  return finishResp.data;
};

export const unlockWithPasskey = async () => {
  const beginResp = await axios.post(`${API}/webauthn/authenticate/begin`, {});
  const assertion = await startAuthentication({ optionsJSON: beginResp.data });
  const finishResp = await axios.post(`${API}/webauthn/authenticate/finish`, {
    credential: assertion,
  });
  return finishResp.data;
};

export const listPasskeys = async (token) => {
  const res = await axios.get(`${API}/webauthn/credentials`, {
    headers: _authHeaders(token),
  });
  return res.data || [];
};

export const revokePasskey = async (token, credentialDocId) => {
  await axios.delete(`${API}/webauthn/credentials/${credentialDocId}`, {
    headers: _authHeaders(token),
  });
};
